import React, { useState, useEffect } from 'react';
import { getRandomQuestions } from '../practiceQuestions';

function MockExam({ onExit, onBackToSIFISO }) {
  const [examStarted, setExamStarted] = useState(false);
  const [examQuestions, setExamQuestions] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [timeRemaining, setTimeRemaining] = useState(40 * 60); // 40 minutes in seconds
  const [examCompleted, setExamCompleted] = useState(false);
  const [score, setScore] = useState(0);
  const [showReview, setShowReview] = useState(false);

  // Timer effect
  useEffect(() => {
    if (!examStarted || examCompleted) return;

    const timer = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 1) {
          handleSubmit();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [examStarted, examCompleted]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleStartExam = () => {
    const questions = getRandomQuestions(30); // Get 30 random questions for PCEP mock
    setExamQuestions(questions);
    setExamStarted(true);
    setTimeRemaining(40 * 60);
  };

  const handleAnswer = (answerIndex) => {
    setSelectedAnswers({
      ...selectedAnswers,
      [currentQuestion]: answerIndex
    });
  };

  const handleNext = () => {
    if (currentQuestion < examQuestions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const handleJumpToQuestion = (index) => {
    setCurrentQuestion(index);
  };

  const handleSubmit = () => {
    let correct = 0;
    examQuestions.forEach((q, index) => {
      if (selectedAnswers[index] === q.correctAnswer) {
        correct++;
      }
    });
    setScore(correct);
    setExamCompleted(true);
  };

  const getAnsweredCount = () => {
    return Object.keys(selectedAnswers).length;
  };

  const isPassed = () => {
    return (score / examQuestions.length) >= 0.7; // 70% to pass
  };

  // Welcome Screen
  if (!examStarted) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="bg-slate-800 rounded-lg p-8 shadow-lg">
          <h1 className="text-4xl font-bold text-white mb-4">
            📝 PCEP Mock Examination
          </h1>
          <p className="text-slate-300 text-lg mb-6">
            Take a full PCEP certification practice exam with real exam conditions.
          </p>

          {/* Exam Details */}
          <div className="bg-slate-700 rounded-lg p-6 mb-6">
            <h2 className="text-2xl font-bold text-white mb-4">Exam Details</h2>
            <div className="space-y-3 text-slate-200">
              <div className="flex items-start">
                <span className="text-blue-400 mr-3">📋</span>
                <div>
                  <strong>Questions:</strong> 30 multiple-choice questions
                </div>
              </div>
              <div className="flex items-start">
                <span className="text-blue-400 mr-3">⏰</span>
                <div>
                  <strong>Time Limit:</strong> 40 minutes
                </div>
              </div>
              <div className="flex items-start">
                <span className="text-blue-400 mr-3">🎯</span>
                <div>
                  <strong>Passing Score:</strong> 70% (21 out of 30 questions)
                </div>
              </div>
              <div className="flex items-start">
                <span className="text-blue-400 mr-3">📚</span>
                <div>
                  <strong>Topics:</strong> All PCEP syllabus blocks (Basics, Data Types, Control Flow, Collections & Functions)
                </div>
              </div>
            </div>
          </div>

          {/* Instructions */}
          <div className="bg-yellow-900/30 border-l-4 border-yellow-500 p-4 mb-6 rounded">
            <h3 className="font-semibold text-yellow-200 mb-2">⚠️ Exam Instructions</h3>
            <ul className="text-yellow-100 text-sm space-y-1">
              <li>• You have 40 minutes to complete all 30 questions</li>
              <li>• You can navigate between questions and change your answers</li>
              <li>• The exam will auto-submit when time expires</li>
              <li>• You need 70% (21/30) to pass</li>
              <li>• Read each question carefully before answering</li>
              <li>• You can review all questions and answers after submission</li>
            </ul>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-4">
            <button
              onClick={handleStartExam}
              className="flex-1 px-6 py-4 rounded-lg font-bold text-lg text-white transition"
              style={{ background: '#008080' }}
            >
              Start Mock Exam →
            </button>
            <button
              onClick={onExit}
              className="px-6 py-4 rounded-lg font-semibold text-white transition"
              style={{ background: '#475569' }}
            >
              Back
            </button>
            <button
              onClick={onBackToSIFISO}
              className="px-6 py-4 rounded-lg font-semibold text-white transition text-sm"
              style={{ background: '#475569' }}
            >
              🏠
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Exam Results Screen
  if (examCompleted && !showReview) {
    const passed = isPassed();
    const percentage = Math.round((score / examQuestions.length) * 100);

    return (
      <div className="max-w-4xl mx-auto">
        <div className="bg-slate-800 rounded-lg p-8 shadow-lg text-center">
          {/* Result Badge */}
          <div className={`inline-block px-8 py-4 rounded-full text-2xl font-bold mb-6 ${
            passed ? 'bg-green-600' : 'bg-red-600'
          }`}>
            {passed ? '✓ PASSED' : '✗ NOT PASSED'}
          </div>

          {/* Score */}
          <h2 className="text-4xl font-bold text-white mb-2">
            {score}/{examQuestions.length}
          </h2>
          <p className="text-2xl text-slate-300 mb-8">
            {percentage}%
          </p>

          {/* Message */}
          <div className="bg-slate-700 rounded-lg p-6 mb-8">
            {passed ? (
              <div>
                <h3 className="text-xl font-bold text-green-400 mb-3">
                  Congratulations! 🎉
                </h3>
                <p className="text-slate-200">
                  You've demonstrated solid understanding of Python fundamentals.
                  You're ready for the real PCEP certification exam!
                </p>
              </div>
            ) : (
              <div>
                <h3 className="text-xl font-bold text-orange-400 mb-3">
                  Keep Practicing! 💪
                </h3>
                <p className="text-slate-200 mb-3">
                  You scored {percentage}%, but need 70% to pass. Don't be discouraged!
                </p>
                <p className="text-slate-300 text-sm">
                  Review the explanations, practice more with targeted questions, and try again when ready.
                </p>
              </div>
            )}
          </div>

          {/* Breakdown */}
          <div className="grid grid-cols-3 gap-4 mb-8 text-center">
            <div className="bg-slate-700 rounded-lg p-4">
              <div className="text-green-400 text-3xl font-bold">{score}</div>
              <div className="text-slate-400 text-sm">Correct</div>
            </div>
            <div className="bg-slate-700 rounded-lg p-4">
              <div className="text-red-400 text-3xl font-bold">{examQuestions.length - score}</div>
              <div className="text-slate-400 text-sm">Incorrect</div>
            </div>
            <div className="bg-slate-700 rounded-lg p-4">
              <div className="text-blue-400 text-3xl font-bold">{examQuestions.length - getAnsweredCount()}</div>
              <div className="text-slate-400 text-sm">Skipped</div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-4 justify-center flex-wrap">
            <button
              onClick={() => setShowReview(true)}
              className="px-6 py-3 rounded-lg font-semibold text-white transition"
              style={{ background: '#008080' }}
            >
              Review Answers →
            </button>
            <button
              onClick={() => {
                setExamStarted(false);
                setExamCompleted(false);
                setCurrentQuestion(0);
                setSelectedAnswers({});
                setScore(0);
              }}
              className="px-6 py-3 rounded-lg font-semibold text-white transition"
              style={{ background: '#475569' }}
            >
              Retake Exam
            </button>
            <button
              onClick={onExit}
              className="px-6 py-3 rounded-lg font-semibold text-white transition"
              style={{ background: '#475569' }}
            >
              Exit
            </button>
            <button
              onClick={onBackToSIFISO}
              className="px-6 py-3 rounded-lg font-semibold text-white transition text-sm"
              style={{ background: '#475569' }}
            >
              🏠 SIFISO
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Review Mode
  if (showReview) {
    const question = examQuestions[currentQuestion];
    const userAnswer = selectedAnswers[currentQuestion];
    const isCorrect = userAnswer === question.correctAnswer;

    return (
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-slate-800 rounded-lg p-4 mb-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-white">
            📝 Review: Question {currentQuestion + 1} of {examQuestions.length}
          </h2>
          <button
            onClick={() => setShowReview(false)}
            className="px-4 py-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-white"
          >
            Back to Results
          </button>
        </div>

        {/* Question Review Card */}
        <div className="bg-slate-800 rounded-lg p-6 mb-4">
          <div className={`inline-block px-3 py-1 rounded-full text-sm font-semibold mb-4 ${
            isCorrect ? 'bg-green-600' : 'bg-red-600'
          }`}>
            {isCorrect ? '✓ Correct' : '✗ Incorrect'}
          </div>

          <h3 className="text-xl font-semibold text-white mb-6">
            {question.question}
          </h3>

          <div className="space-y-3">
            {question.options.map((option, index) => {
              const isUserAnswer = userAnswer === index;
              const isCorrectAnswer = question.correctAnswer === index;

              return (
                <div
                  key={index}
                  className={`p-4 rounded-lg border-2 ${
                    isCorrectAnswer
                      ? 'border-green-500 bg-green-900/30'
                      : isUserAnswer
                      ? 'border-red-500 bg-red-900/30'
                      : 'border-slate-600 bg-slate-700'
                  }`}
                >
                  <div className="flex items-center">
                    <span className="font-semibold mr-2">
                      {String.fromCharCode(65 + index)}.
                    </span>
                    <span>{option}</span>
                    {isCorrectAnswer && (
                      <span className="ml-auto text-green-400 font-semibold">✓ Correct Answer</span>
                    )}
                    {isUserAnswer && !isCorrectAnswer && (
                      <span className="ml-auto text-red-400 font-semibold">✗ Your Answer</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Explanation */}
          <div className="mt-6 bg-blue-900/30 border-l-4 border-blue-500 p-4 rounded">
            <h4 className="font-semibold text-blue-200 mb-2">💡 Explanation</h4>
            <p className="text-blue-100">{question.explanation}</p>
          </div>
        </div>

        {/* Navigation */}
        <div className="flex justify-between items-center">
          <button
            onClick={handlePrevious}
            disabled={currentQuestion === 0}
            className={`px-6 py-3 rounded-lg font-semibold transition ${
              currentQuestion === 0
                ? 'bg-slate-700 text-slate-500 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-700 text-white'
            }`}
          >
            ← Previous
          </button>

          <span className="text-slate-400">
            {currentQuestion + 1} / {examQuestions.length}
          </span>

          <button
            onClick={handleNext}
            disabled={currentQuestion === examQuestions.length - 1}
            className={`px-6 py-3 rounded-lg font-semibold transition ${
              currentQuestion === examQuestions.length - 1
                ? 'bg-slate-700 text-slate-500 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-700 text-white'
            }`}
          >
            Next →
          </button>
        </div>
      </div>
    );
  }

  // Active Exam Screen
  const question = examQuestions[currentQuestion];
  const hasAnswered = selectedAnswers[currentQuestion] !== undefined;
  const answeredCount = getAnsweredCount();

  return (
    <div className="max-w-6xl mx-auto">
      {/* Exam Header */}
      <div className="bg-slate-800 rounded-lg p-4 mb-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-white">PCEP Mock Examination</h2>
            <p className="text-slate-400 text-sm">
              Question {currentQuestion + 1} of {examQuestions.length} |
              Answered: {answeredCount}/{examQuestions.length}
            </p>
          </div>
          <div className={`text-3xl font-bold ${
            timeRemaining < 300 ? 'text-red-400' : 'text-blue-400'
          }`}>
            ⏰ {formatTime(timeRemaining)}
          </div>
        </div>
      </div>

      <div className="flex gap-4">
        {/* Question Navigator Sidebar */}
        <div className="w-48 bg-slate-800 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-slate-400 mb-3">Questions</h3>
          <div className="grid grid-cols-5 gap-2">
            {examQuestions.map((_, index) => (
              <button
                key={index}
                onClick={() => handleJumpToQuestion(index)}
                className={`w-10 h-10 rounded-lg text-sm font-semibold transition ${
                  index === currentQuestion
                    ? 'bg-blue-600 text-white'
                    : selectedAnswers[index] !== undefined
                    ? 'bg-green-600 text-white'
                    : 'bg-slate-700 text-slate-400 hover:bg-slate-600'
                }`}
              >
                {index + 1}
              </button>
            ))}
          </div>
          <div className="mt-4 space-y-2 text-xs text-slate-400">
            <div className="flex items-center">
              <div className="w-4 h-4 bg-blue-600 rounded mr-2"></div>
              <span>Current</span>
            </div>
            <div className="flex items-center">
              <div className="w-4 h-4 bg-green-600 rounded mr-2"></div>
              <span>Answered</span>
            </div>
            <div className="flex items-center">
              <div className="w-4 h-4 bg-slate-700 rounded mr-2"></div>
              <span>Unanswered</span>
            </div>
          </div>
        </div>

        {/* Question Panel */}
        <div className="flex-1 bg-slate-800 rounded-lg p-6">
          <h3 className="text-xl font-semibold text-white mb-6">
            {question.question}
          </h3>

          <div className="space-y-3 mb-8">
            {question.options.map((option, index) => (
              <button
                key={index}
                onClick={() => handleAnswer(index)}
                className={`w-full p-4 rounded-lg border-2 transition text-left ${
                  selectedAnswers[currentQuestion] === index
                    ? 'border-blue-500 bg-blue-900/30'
                    : 'border-slate-600 bg-slate-700 hover:border-slate-500'
                }`}
              >
                <span className="font-semibold mr-2">
                  {String.fromCharCode(65 + index)}.
                </span>
                {option}
              </button>
            ))}
          </div>

          {/* Navigation Buttons */}
          <div className="flex justify-between items-center">
            <button
              onClick={handlePrevious}
              disabled={currentQuestion === 0}
              className={`px-6 py-3 rounded-lg font-semibold transition ${
                currentQuestion === 0
                  ? 'bg-slate-700 text-slate-500 cursor-not-allowed'
                  : 'bg-slate-700 hover:bg-slate-600 text-white'
              }`}
            >
              ← Previous
            </button>

            {currentQuestion === examQuestions.length - 1 ? (
              <button
                onClick={handleSubmit}
                className="px-8 py-3 rounded-lg font-bold bg-green-600 hover:bg-green-700 text-white transition"
              >
                Submit Exam →
              </button>
            ) : (
              <button
                onClick={handleNext}
                className="px-6 py-3 rounded-lg font-semibold bg-blue-600 hover:bg-blue-700 text-white transition"
              >
                Next →
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default MockExam;
